import filetype from 'magic-bytes.js'
import { MediaDimensions } from './dropZoneUploader.types'
import { FILE_CHUNK_SIZE } from './dropZoneUploader.constants'

export const MAX_AUDIO_DURATION_SECONDS = 5400

export const normalizeMediaKey = (mediaKey: string) => {
  if (!mediaKey.includes('/')) {
    return mediaKey
  }

  const [, key] = mediaKey?.split('/') || []

  return key
}

export const getImageDimensions = (url: string): Promise<MediaDimensions> | void => {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.src = url

    img.onload = (event: Event) => {
      const image = event.target as HTMLImageElement

      resolve({
        width: image.width,
        height: image.height
      })
    }

    img.onerror = () => {
      reject(new Error('Failed to load the image.'))
    }
  })
}

export const getMimeType = async (file: File): Promise<string | undefined> => {
  try {
    const fileExtension = file.name.toLowerCase()

    if (fileExtension.endsWith('.m4a')) {
      return 'audio/x-m4a'
    }

    const chunk = file.slice(0, FILE_CHUNK_SIZE)

    const buffer = await chunk.arrayBuffer()
    const bytes = new Uint8Array(buffer)

    return filetype(bytes)?.[0]?.mime || file.type
  } catch (error) {
    throw new Error(`${error}`)
  }
}

export const filesWithOriginalType = async (files: File[]): Promise<File[]> => {
  const filesWithMimeType = await Promise.all(
    files.map(async (file) => {
      const mimeType = await getMimeType(file)

      return new File([file], file.name, {
        type: mimeType || file.type
      })
    })
  )

  return filesWithMimeType
}

export const getAudioDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio()
    const url = URL.createObjectURL(file)

    audio.src = url

    audio.onloadedmetadata = (event: Event) => {
      const audioElement = event.target as HTMLAudioElement

      URL.revokeObjectURL(url)

      if (
        !isFinite(audioElement.duration) ||
        audioElement.duration <= 0 ||
        isNaN(audioElement.duration)
      ) {
        reject(new Error('Invalid audio duration'))
        return
      }

      resolve(audioElement.duration)
    }

    audio.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load the audio file'))
    }
  })
}

export const isValidAudioDuration = async (file: File): Promise<boolean> => {
  try {
    const durationInSeconds = await getAudioDuration(file)
    return durationInSeconds <= MAX_AUDIO_DURATION_SECONDS
  } catch {
    return false
  }
}
